
import React, { useEffect, useState } from "react";
import { Modal, Pagination } from "react-bootstrap";
import "../styles/admindocument.css";

// Mapa de tipos de backend a frontend (para URLs y display)
const tipoMap = {
  "Selfie": "selfie",
  "DNI/Frente": "dniFront",
  "DNI/Dorso": "dniBack",
  "Carnet/Frente": "carnetFront",
  "Carnet/Dorso": "carnetBack"
};

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');
const LEGAJOS_POR_PAGINA = 5;

// Helper para obtener headers de auth con JWT
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('⚠️ No hay token de autenticación');
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

function AdminDocuments() {
  const [docs, setDocs] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  const loadDocuments = () => {
    setLoading(true);
    fetch(`${API_URL}/api/fotos/documents`, {
      headers: getAuthHeaders()
    })
      .then(res => {
        if (!res.ok) throw new Error("Error al cargar documentos");
        return res.json();
      })
      .then(data => {
        setDocs(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error cargando documentos:", err);
        setError("No se pudieron cargar los documentos");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleDeleteOne = async (legajo, tipo, filename) => {
    if (!window.confirm(`¿Eliminar ${filename}?`)) return;
    
    setDeleting(true);
    const backendTipo = tipoMap[tipo] || tipo;
    
    try {
      const res = await fetch(`${API_URL}/api/fotos/delete/${legajo}/${backendTipo}/${filename}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      
      if (data.success) {
        loadDocuments();
        alert("✅ Imagen eliminada correctamente");
      } else {
        alert(`❌ Error al eliminar imagen: ${data.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error("❌ Error al eliminar imagen:", err);
      alert("❌ Error de conexión al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async (legajo) => {
    if (!window.confirm(`¿Eliminar TODAS las imágenes del legajo ${legajo}?`)) return;
    
    setDeleting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/fotos/delete-all/${legajo}`, { 
        method: "DELETE",
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }
      
      const data = await res.json();
      
      if (data.success) {
        loadDocuments();
        alert("✅ Carpeta eliminada correctamente");
      } else {
        alert("❌ Error al eliminar carpeta");
      }
    } catch (err) {
      console.error("❌ Error al eliminar carpeta:", err);
      alert("❌ Error de conexión al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const openImageAsPDF = (url, filename) => {
    // Abrir endpoint que convierte la imagen a PDF
    const pdfUrl = `${API_URL}api/fotos/view-pdf?imageUrl=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;
    window.open(pdfUrl, '_blank');
  };

  const filteredLegajos = Object.entries(docs).filter(([legajo]) =>
    legajo.toLowerCase().includes(search.toLowerCase())
  );

  // Paginación
  const totalPages = Math.ceil(filteredLegajos.length / LEGAJOS_POR_PAGINA);
  const startIndex = (currentPage - 1) * LEGAJOS_POR_PAGINA;
  const endIndex = startIndex + LEGAJOS_POR_PAGINA;
  const currentLegajos = filteredLegajos.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  if (loading) return <div className="loading">⏳ Cargando documentos...</div>;
  if (error) return <div className="error">❌ Error: {error}</div>;

  return (
    <div className="admin-documents">
      <div className="header-section">
        <h2>📂 Gestión de Documentos</h2>
        <p className="subtitle">Total: {filteredLegajos.length} legajos</p>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔎 Buscar por legajo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn-secondary" onClick={() => setSearch("")}>Limpiar</button>
        <button className="btn-primary" onClick={loadDocuments} disabled={loading}>
          🔄 Recargar
        </button>
      </div>

      {deleting && (
        <div className="alert alert-info">⏳ Eliminando archivo...</div>
      )}

      <div className="documents-container">
        {currentLegajos.map(([legajo, files]) => (
          <div className="legajo-card" key={legajo}>
            <div className="legajo-header">
              <div className="legajo-title">
                <h3>📁 Legajo {legajo}</h3>
                <span className="badge">{files.length} {files.length === 1 ? 'archivo' : 'archivos'}</span>
              </div>
              <button 
                className="btn-delete-all"
                onClick={() => handleDeleteAll(legajo)}
                disabled={deleting}
                title="Eliminar todas las imágenes"
              >
                🗑️ Eliminar todo
              </button>
            </div>

            <div className="images-grid">
              {files.map((f, i) => (
                <div className="image-card" key={`${legajo}-${i}`}>
                  <div className="image-thumbnail" onClick={() => setSelectedImage(f)}>
                    <img src={f.url} alt={f.name} />
                    <div className="image-overlay">
                      <span>👁️ Ver</span>
                    </div>
                  </div>
                  <div className="image-info">
                    <p className="image-type">{f.tipo}</p>
                    <div className="image-actions">
                      <button 
                        className="btn-view-pdf"
                        onClick={() => openImageAsPDF(f.url, f.name)}
                        title="Abrir como PDF"
                      >
                        📄 PDF
                      </button>
                      <button 
                        className="btn-delete-one"
                        onClick={() => handleDeleteOne(legajo, f.tipo, f.name)}
                        disabled={deleting}
                        title="Eliminar imagen"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredLegajos.length === 0 && (
        <div className="no-documents">
          <p>📭 No hay documentos para mostrar</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-container">
          <Pagination>
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} />
            
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <Pagination.Ellipsis key={pageNum} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      {/* Modal de vista previa */}
      <Modal show={!!selectedImage} onHide={() => setSelectedImage(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedImage?.tipo}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage && (
            <>
              <img src={selectedImage.url} alt={selectedImage.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
              <p className="mt-3 text-muted">{selectedImage.name}</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setSelectedImage(null)}>Cerrar</button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              if (selectedImage) {
                openImageAsPDF(selectedImage.url, selectedImage.name);
              }
            }}
          >
            📄 Abrir como PDF
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminDocuments;
